import { create } from "zustand";
import { AppRecordJK, AppRecordStatusJK, RequestApi, RouteApi, StatusMsg } from "utils";
import { AppLog } from "services";
import { Service } from "utils/services";
import { requestSubmitFollowingUser } from "api-function/requestFriendStatusApi";
import { onPostReaction } from "api-function/postContentApi";
import { PostReaction } from "models/postReaction";

interface ProfileState {
  userId: string | null;
  flag: string | null;
  user: any;
  userPageInfomation: any;
  userCoverPictures: any[];
  friendUsers: any;
  pageLoading: boolean;
  profileSwtichFlagLoading: boolean;
  feedPosts: any[];
  feedPostsRecord: AppRecordJK;
  feedPostIds: Set<string>;
  posts: any[];
  postsRecord: AppRecordJK;
  postIds: Set<string>;
  shares: any[];
  sharesRecord: AppRecordJK;
  shareIds: Set<string>;
  likes: any[];
  likesRecord: AppRecordJK;
  likeIds: Set<string>;
  loves: any[];
  lovesRecord: AppRecordJK;
  loveIds: Set<string>;
  initializePage: (userId: string | null, flag: string) => Promise<void>;
  fetchProfileView: () => Promise<void>;
  fetchProfileViewPost: (isRefresh: boolean) => Promise<void>;
  onClickedLike: (post: any) => Promise<void>;
  onClickedFollow: (post: any, userId: any) => Promise<void>;
  onClickedShare: (post: any) => Promise<void>;
  updateContentByFlag: (flag: string) => Promise<void>;
  updateUser: (updates: Partial<any>) => void;

  onSubmitUnpublishToPublished: ({
    inputPassword,
    setIsDialogUpdatePageStatus,
    setLoadingUpdatePageStatus,
    setValidateText,
  }: {
    inputPassword: string;
    setIsDialogUpdatePageStatus: (value: boolean) => void;
    setLoadingUpdatePageStatus: (value: boolean) => void;
    setValidateText: (value: string) => void;
  }) => Promise<void>;

  onSubmitPublishedToUnpublish: ({
    inputPassword,
    setIsDialogUpdatePageStatus,
    setLoadingUpdatePageStatus,
    setValidateText,
  }: {
    inputPassword: string;
    setIsDialogUpdatePageStatus: (value: boolean) => void;
    setLoadingUpdatePageStatus: (value: boolean) => void;
    setValidateText: (value: string) => void;
  }) => Promise<void>;

  onSubmitDeletePage: ({
    inputPassword,
    setIsDialogUpdatePageStatus,
    setLoadingUpdatePageStatus,
    setValidateText,
  }: {
    inputPassword: string;
    setIsDialogUpdatePageStatus: (value: boolean) => void;
    setLoadingUpdatePageStatus: (value: boolean) => void;
    setValidateText: (value: string) => void;
  }) => Promise<void>;

  fetchGetPageInformation: () => Promise<void>;

  onPostFeedUpdate: (postUpdate: any) => Promise<void>;
}

const useProfileStore = create<ProfileState>((set, get) => ({
  userId: null,
  flag: null,
  user: null,
  userCoverPictures: [],
  friendUsers: null,
  pageLoading: true,
  userPageInfomation: null,
  profileSwtichFlagLoading: false,

  feedPosts: [],
  feedPostsRecord: new AppRecordJK(),
  feedPostIds: new Set<string>(),

  posts: [],
  postsRecord: new AppRecordJK(),
  postIds: new Set<string>(),

  shares: [],
  sharesRecord: new AppRecordJK(),
  shareIds: new Set<string>(),

  likes: [],
  likesRecord: new AppRecordJK(),
  likeIds: new Set<string>(),

  loves: [],
  lovesRecord: new AppRecordJK(),
  loveIds: new Set<string>(),

  updateUser: (newUserData) =>
    set((state) => {
      if (!state.user) return { user: newUserData };

      const updatedUser: any = { ...state.user };
      let changed = false;

      for (const key in newUserData) {
        const oldVal = state.user[key];
        const newVal = newUserData[key];

        if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
          updatedUser[key] = newVal;
          changed = true;
        }
      }

      return changed ? { user: updatedUser } : {};
    }),

  initializePage: async (userId: string | null, flag: string) => {
    try {
      set({
        userId: userId,
        flag: flag,
        user: null,
        pageLoading: true,
        userCoverPictures: [],
        friendUsers: null,
        profileSwtichFlagLoading: false,

        feedPosts: [],
        feedPostsRecord: new AppRecordJK(),
        feedPostIds: new Set<string>(),

        posts: [],
        postsRecord: new AppRecordJK(),
        postIds: new Set<string>(),

        shares: [],
        sharesRecord: new AppRecordJK(),
        shareIds: new Set<string>(),

        likes: [],
        likesRecord: new AppRecordJK(),
        likeIds: new Set<string>(),

        loves: [],
        lovesRecord: new AppRecordJK(),
        loveIds: new Set<string>(),
      });

      await useProfileStore.getState().fetchProfileView();
    } catch (error) {
      AppLog.show({
        message: `[initializePage]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  updateContentByFlag: async (flag: string) => {
    try {
      const { posts, feedPosts, shares, likes, loves } = get();
      set({ flag: flag });
      if (flag === "feed" && feedPosts.length === 0) {
        await useProfileStore.getState().fetchProfileViewPost(true);
      } else if (flag === "posts" && posts.length === 0) {
        await useProfileStore.getState().fetchProfileViewPost(true);
      } else if (flag === "share" && shares.length === 0) {
        await useProfileStore.getState().fetchProfileViewPost(true);
      } else if (flag === "like" && likes.length === 0) {
        await useProfileStore.getState().fetchProfileViewPost(true);
      } else if (flag === "love" && loves.length === 0) {
        await useProfileStore.getState().fetchProfileViewPost(true);
      }
    } catch (error) {
      AppLog.show({
        message: `[updateContentByFlag]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  fetchProfileView: async () => {
    try {
      const { userId, flag } = get();

      let url = RouteApi.getProfileView;
      url += `token=${Service.token}`;

      const data = {
        user_id: userId,
        flag: flag,
      };

      const responses = await RequestApi(url, { data: data });
      if (responses.status === "success") {
        set({
          user: responses.user,
          userCoverPictures: responses.user_cover_pictures,
          friendUsers: responses.friend_users,
        });
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[fetchProfileView]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      set({ pageLoading: false });
    }
  },

  fetchProfileViewPost: async (isRefresh: boolean) => {
    const {
      userId,
      feedPosts,
      feedPostsRecord,
      feedPostIds,
      flag,
      posts,
      postsRecord,
      postIds,
      shares,
      sharesRecord,
      shareIds,
      likes,
      likesRecord,
      likeIds,
      loves,
      lovesRecord,
      loveIds,
    } = get();

    let tmpFeedPosts = [...feedPosts];
    let tmpFeedPostIds = new Set(feedPostIds);
    let tmpFeedPostsRecord = new AppRecordJK({
      ...feedPostsRecord,
    });

    let tmpPosts = [...posts];
    let tmpPostIds = new Set(postIds);
    let tmpPostsRecord = new AppRecordJK({
      ...postsRecord,
    });

    let tmpShares = [...shares];
    let tmpShareIds = new Set(shareIds);
    let tmpSharesRecord = new AppRecordJK({
      ...sharesRecord,
    });

    let tmpLoves = [...loves];
    let tmpLoveIds = new Set(loveIds);
    let tmpLovesRecord = new AppRecordJK({
      ...lovesRecord,
    });

    let tmpLikes = [...likes];
    let tmpLikeIds = new Set(likeIds);
    let tmpLikesRecord = new AppRecordJK({
      ...likesRecord,
    });

    try {
      let url = RouteApi.getProfileViewPost;
      url += `token=${Service.token}`;

      if (Service.isAuthenticated) {
        if (isRefresh) {
          if (flag === "feed") {
            tmpFeedPosts = [];
            tmpFeedPostIds = new Set<string>();
            tmpFeedPostsRecord.loadingStatus = AppRecordStatusJK.loading;

            set({
              feedPosts: tmpFeedPosts,
              feedPostIds: tmpFeedPostIds,
              feedPostsRecord: tmpFeedPostsRecord,
            });
          } else if (flag === "posts") {
            set({ profileSwtichFlagLoading: true });
            tmpPosts = [];
            tmpPostIds = new Set<string>();
            tmpPostsRecord.loadingStatus = AppRecordStatusJK.loading;

            set({
              posts: tmpPosts,
              postIds: tmpPostIds,
              postsRecord: tmpPostsRecord,
            });
          } else if (flag === "share") {
            set({ profileSwtichFlagLoading: true });
            tmpShares = [];
            tmpShareIds = new Set<string>();
            tmpSharesRecord.loadingStatus = AppRecordStatusJK.loading;

            set({
              shares: tmpShares,
              shareIds: tmpShareIds,
              sharesRecord: tmpSharesRecord,
            });
          } else if (flag === "love") {
            set({ profileSwtichFlagLoading: true });
            tmpLoves = [];
            tmpLoveIds = new Set<string>();
            tmpLovesRecord.loadingStatus = AppRecordStatusJK.loading;

            set({
              loves: tmpLoves,
              loveIds: tmpLoveIds,
              lovesRecord: tmpLovesRecord,
            });
          } else if (flag === "like") {
            set({ profileSwtichFlagLoading: true });
            tmpLikes = [];
            tmpLikeIds = new Set<string>();
            tmpLikesRecord.loadingStatus = AppRecordStatusJK.loading;

            set({
              likes: tmpLikes,
              likeIds: tmpLikeIds,
              likesRecord: tmpLikesRecord,
            });
          }
        } else {
          if (flag === "feed") {
            if (tmpFeedPostsRecord.canLoadMore()) {
              tmpFeedPostsRecord.loadingStatus = AppRecordStatusJK.loadingMore;
              set({ feedPostsRecord: tmpFeedPostsRecord });
            } else {
              return;
            }
          } else if (flag === "posts") {
            if (tmpPostsRecord.canLoadMore()) {
              tmpPostsRecord.loadingStatus = AppRecordStatusJK.loadingMore;
              set({ postsRecord: tmpPostsRecord });
            } else {
              return;
            }
          } else if (flag === "share") {
            if (tmpSharesRecord.canLoadMore()) {
              tmpSharesRecord.loadingStatus = AppRecordStatusJK.loadingMore;
              set({ sharesRecord: tmpSharesRecord });
            } else {
              return;
            }
          } else if (flag === "love") {
            if (tmpLovesRecord.canLoadMore()) {
              tmpLovesRecord.loadingStatus = AppRecordStatusJK.loadingMore;
              set({ lovesRecord: tmpLovesRecord });
            } else {
              return;
            }
          } else if (flag === "like") {
            if (tmpLikesRecord.canLoadMore()) {
              tmpLikesRecord.loadingStatus = AppRecordStatusJK.loadingMore;
              set({ likesRecord: tmpLikesRecord });
            } else {
              return;
            }
          }
        }

        let feedPostIdsArray: string[] = [...tmpFeedPostIds];
        let postIdsArray: string[] = [...tmpPostIds];
        let shareIdsArray: string[] = [...tmpShareIds];
        let loveIdsArray: string[] = [...tmpLoveIds];
        let likeIdsArray: string[] = [...tmpLikeIds];

        const checkPostId = () => {
          let postId;
          if (flag === "feed") {
            postId = feedPostIdsArray;
          } else if (flag === "posts") {
            postId = postIdsArray;
          } else if (flag === "share") {
            postId = shareIdsArray;
          } else if (flag === "love") {
            postId = loveIdsArray;
          } else if (flag === "like") {
            postId = likeIdsArray;
          }

          return postId;
        };

        const data = {
          user_id: userId,
          flag: flag === "posts" ? "post" : flag,
          post_ids: checkPostId(),
        };

        const responses = await RequestApi(url, { data });
        if (responses.status === "success") {
          const newPosts: any[] = responses.posts;

          if (flag === "feed") {
            const filteredPosts = newPosts.filter((post) => !tmpFeedPostIds.has(post.provider_post_id));
            filteredPosts.forEach((post) => tmpFeedPostIds.add(post.provider_post_id));
            tmpFeedPostsRecord.loadingStatus = filteredPosts.length > 0 ? AppRecordStatusJK.loaded : AppRecordStatusJK.noLoadMore;
            const postIdsRes = new Set(newPosts.map((post) => post.provider_post_id));
            set({
              feedPosts: [...tmpFeedPosts, ...filteredPosts],
              feedPostIds: postIdsRes,
              feedPostsRecord: tmpFeedPostsRecord,
            });
          } else if (flag === "posts") {
            const filteredPosts = newPosts.filter((post) => !tmpPostIds.has(post.provider_post_id));
            filteredPosts.forEach((post) => tmpPostIds.add(post.provider_post_id));
            tmpPostsRecord.loadingStatus = filteredPosts.length > 0 ? AppRecordStatusJK.loaded : AppRecordStatusJK.noLoadMore;
            const postIdsRes = new Set(newPosts.map((post) => post.provider_post_id));
            set({
              posts: [...tmpPosts, ...filteredPosts],
              postIds: postIdsRes,
              postsRecord: tmpPostsRecord,
            });
          } else if (flag === "share") {
            const filteredPosts = newPosts.filter((post) => !tmpShareIds.has(post.provider_post_id));
            filteredPosts.forEach((post) => tmpShareIds.add(post.provider_post_id));
            tmpSharesRecord.loadingStatus = filteredPosts.length > 0 ? AppRecordStatusJK.loaded : AppRecordStatusJK.noLoadMore;
            const postIdsRes = new Set(newPosts.map((post) => post.provider_post_id));

            set({
              shares: [...tmpShares, ...filteredPosts],
              shareIds: postIdsRes,
              sharesRecord: tmpSharesRecord,
            });
          } else if (flag === "love") {
            const filteredPosts = newPosts.filter((post) => !tmpLoveIds.has(post.provider_post_id));
            filteredPosts.forEach((post) => tmpLoveIds.add(post.provider_post_id));
            tmpLovesRecord.loadingStatus = filteredPosts.length > 0 ? AppRecordStatusJK.loaded : AppRecordStatusJK.noLoadMore;
            const postIdsRes = new Set(newPosts.map((post) => post.provider_post_id));

            set({
              loves: [...tmpLoves, ...filteredPosts],
              loveIds: postIdsRes,
              lovesRecord: tmpLovesRecord,
            });
          } else if (flag === "like") {
            const filteredPosts = newPosts.filter((post) => !tmpLikeIds.has(post.provider_post_id));
            filteredPosts.forEach((post) => tmpLikeIds.add(post.provider_post_id));
            tmpLikesRecord.loadingStatus = filteredPosts.length > 0 ? AppRecordStatusJK.loaded : AppRecordStatusJK.noLoadMore;
            const postIdsRes = new Set(newPosts.map((post) => post.provider_post_id));

            set({
              likes: [...tmpLikes, ...filteredPosts],
              likeIds: postIdsRes,
              likesRecord: tmpLikesRecord,
            });
          }
          set({ profileSwtichFlagLoading: false });
        } else {
          if (flag === "feed") {
            tmpFeedPostsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
            set({ feedPostsRecord: tmpFeedPostsRecord });
          } else if (flag === "posts") {
            tmpPostsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
            set({ postsRecord: tmpPostsRecord });
          } else if (flag === "share") {
            tmpSharesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
            set({ sharesRecord: tmpSharesRecord });
          } else if (flag === "love") {
            tmpLovesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
            set({ lovesRecord: tmpLovesRecord });
          } else if (flag === "like") {
            tmpLikesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
            set({ likesRecord: tmpLikesRecord });
          }
          set({ profileSwtichFlagLoading: false });
        }
      }
    } catch (error) {
      if (flag === "feed") {
        tmpFeedPostsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ feedPostsRecord: tmpFeedPostsRecord });
      } else if (flag === "posts") {
        tmpPostsRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ postsRecord: tmpPostsRecord });
      } else if (flag === "share") {
        tmpSharesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ sharesRecord: tmpSharesRecord });
      } else if (flag === "love") {
        tmpLovesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ lovesRecord: tmpLovesRecord });
      } else if (flag === "like") {
        tmpLikesRecord.loadingStatus = AppRecordStatusJK.noLoadMore;
        set({ likesRecord: tmpLikesRecord });
      }

      AppLog.show({
        message: `[fetchProfileViewPost]`,
        status: StatusMsg.error,
        error,
      });
      set({ profileSwtichFlagLoading: false });
    }
  },

  onSubmitUnpublishToPublished: async ({
    inputPassword,
    setIsDialogUpdatePageStatus,
    setLoadingUpdatePageStatus,
    setValidateText,
  }: {
    inputPassword: string;
    setIsDialogUpdatePageStatus: (value: boolean) => void;
    setLoadingUpdatePageStatus: (value: boolean) => void;
    setValidateText: (value: string) => void;
  }) => {
    const { userId, user } = get();
    try {
      setLoadingUpdatePageStatus(true);
      let url: string = "";
      url = RouteApi.submitSendReviewPageInformation;
      url += `token=${Service.token}`;

      const data = {
        page_id: userId,
        password: inputPassword,
      };

      await RequestApi(url, { data }).then((responses) => {
        if (responses.status === "success") {
          const updateUserPage = {
            ...user,
            status: responses.user_page.status,
          };
          set({
            user: updateUserPage,
          });
          setIsDialogUpdatePageStatus(false);
        } else {
          setValidateText(responses.msg);
        }
        setLoadingUpdatePageStatus(false);
      });

      setLoadingUpdatePageStatus(false);
    } catch (error) {
      AppLog.show({
        message: `[onSubmitUnpublishToPublished]`,
        status: StatusMsg.error,
        error,
      });
      setLoadingUpdatePageStatus(false);
    }
  },

  onSubmitPublishedToUnpublish: async ({
    inputPassword,
    setIsDialogUpdatePageStatus,
    setLoadingUpdatePageStatus,
    setValidateText,
  }: {
    inputPassword: string;
    setIsDialogUpdatePageStatus: (value: boolean) => void;
    setLoadingUpdatePageStatus: (value: boolean) => void;
    setValidateText: (value: string) => void;
  }) => {
    const { user } = get();
    try {
      setLoadingUpdatePageStatus(true);
      let url: string = "";
      url = RouteApi.submitUnpublishPageInformation;
      url += `token=${Service.token}`;

      const data = {
        page_id: user.id,
        password: inputPassword,
      };

      await RequestApi(url, { data }).then((responses) => {
        if (responses.status === "success") {
          const updateUserPage = {
            ...user,
            status: responses.user_page.status, // Corrected the syntax
          };
          set({
            user: updateUserPage,
          });
          setIsDialogUpdatePageStatus(false);
        } else {
          setValidateText(responses.msg);
        }
        setLoadingUpdatePageStatus(false);
      });

      setLoadingUpdatePageStatus(false);
    } catch (error) {
      AppLog.show({
        message: `[onSubmitUnpublishToPublished]`,
        status: StatusMsg.error,
        error,
      });
      setLoadingUpdatePageStatus(false);
    }
  },

  onSubmitDeletePage: async ({
    inputPassword,
    setIsDialogUpdatePageStatus,
    setLoadingUpdatePageStatus,
    setValidateText,
  }: {
    inputPassword: string;
    setIsDialogUpdatePageStatus: (value: boolean) => void;
    setLoadingUpdatePageStatus: (value: boolean) => void;
    setValidateText: (value: string) => void;
  }) => {
    const { user } = get();
    try {
      setLoadingUpdatePageStatus(true);
      let url: string = "";
      url = RouteApi.submitDeletePageInformation;
      url += `token=${Service.token}`;

      const data = {
        page_id: user.id,
        password: inputPassword,
      };

      await RequestApi(url, { data }).then((responses) => {
        if (responses.status === "success") {
          const updateUserPage = {
            ...user,
            status: responses.user_page.status, // Corrected the syntax
          };
          set({
            user: updateUserPage,
          });
          setIsDialogUpdatePageStatus(false);
        } else {
          setValidateText(responses.msg);
        }
        setLoadingUpdatePageStatus(false);
      });

      setLoadingUpdatePageStatus(false);
    } catch (error: any) {
      AppLog.show({
        message: `[onSubmitUnpublishToPublished]`,
        status: StatusMsg.error,
        error,
      });
      setLoadingUpdatePageStatus(false);
    }
  },

  fetchGetPageInformation: async () => {
    const { user } = get();
    try {
      let url: string = "";
      url = RouteApi.getPageInformation;
      url += `token=${Service.token}`;

      const data = {
        user_id: user.id,
      };

      await RequestApi(url, { data }).then((responses) => {
        if (responses.status === "success") {
          set({
            userPageInfomation: responses.user,
          });
        }
      });
    } catch (error) {
      AppLog.show({
        message: `[getPageInformation]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedLike: async (post: any) => {
    try {
      const { feedPosts } = get();
      if (Service.userAuth) {
        const attachment: any = post?.post_attachments[0];
        const isLike: boolean = !Boolean(Service.toInteger(post?.is_reaction_like));

        const responses = await onPostReaction({
          content: post,
          reaction: PostReaction.like,
          isLike: isLike,
          isFavorite: false,
          attachment,
        });
        if (responses.status === "success") {
          let tmpPost = structuredClone(post);
          tmpPost.is_reaction_like = isLike ? "1" : "0";
          const numberLike = isLike ? 1 : -1;
          tmpPost.reaction_like_count = Service.toInteger(tmpPost.reaction_like_count) + numberLike;

          if (isLike) {
            const userAuth = { ...Service.userAuth };
            tmpPost.like_users = [...tmpPost.like_users, userAuth];
          } else {
            tmpPost.like_users = tmpPost.like_users.filter((user: any) => user.id.toString() !== Service.userAuth.id.toString());
          }

          const editIndex = feedPosts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
          if (editIndex !== -1) {
            set((state) => {
              const updatedPosts = [...state.feedPosts];
              updatedPosts[editIndex] = tmpPost;
              return { feedPosts: updatedPosts };
            });
          }
        } else {
        }
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedLike]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedFollow: async (post: any, userId: string) => {
    try {
      const { feedPosts } = get();

      const responses = await requestSubmitFollowingUser({ userId: userId });
      if (responses.status === "success") {
        let tmpPost = structuredClone(post);

        if (tmpPost.re_post_user.id.toString() === userId) {
          tmpPost.re_post_user.friend_status = responses.user?.friend_status ?? null;
        }
        const editIndex = feedPosts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
        if (editIndex !== -1) {
          set((state) => {
            const updatedPosts = [...state.feedPosts];
            updatedPosts[editIndex] = tmpPost;
            return { feedPosts: updatedPosts };
          });
        }
      } else {
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedFollow]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onClickedShare: async (post: any) => {
    try {
      const { feedPosts } = get();
      const tmpPost = { ...post };

      const editIndex = feedPosts.findIndex((post) => post.provider_post_id === tmpPost.provider_post_id);
      if (editIndex !== -1) {
        set((state) => {
          const updatedPosts = [...state.feedPosts];
          updatedPosts[editIndex] = tmpPost;
          return { feedPosts: updatedPosts };
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[onClickedShare]`,
        status: StatusMsg.error,
        error,
      });
    }
  },

  onPostFeedUpdate: async (postUpdate: any) => {
    try {
      const { feedPosts } = get();
      const editIndex = feedPosts.findIndex((post) => post.provider_post_id === postUpdate.provider_post_id);
      if (editIndex !== -1) {
        set((state) => {
          const updatedPosts = [...state.feedPosts];
          updatedPosts[editIndex] = postUpdate;
          return { feedPosts: updatedPosts };
        });
      }
    } catch (error) {
      AppLog.show({
        message: `[onPostFeedUpdate]`,
        status: StatusMsg.error,
        error,
      });
    }
  },
}));

export default useProfileStore;

